import { Injectable, OnDestroy } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  Subject,
  of
} from 'rxjs';
import { catchError, filter, finalize, map, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AlertType, SweetAlertService } from 'src/app/shared/Utilities/sweet-alert.service';
import { AccountService as CMSAccountService } from 'src/app/shared/api-services/cms/services/account.service';
import { LoaderHandlerService } from 'src/app/shared/services/loader-handler.service';
import { IDecodedToken, ILoginResponse } from 'src/app/shared/api-services/user-mgt/models/account.model';
import { IAccount } from 'src/app/shared/api-services/cms/models/account.model';
import { AccountService } from 'src/app/shared/api-services/user-mgt/services/account.service';
import { Role, UserModel } from 'src/app/shared/interfaces/user';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { STORAGE_KEYS } from 'src/app/shared/Utilities/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private userDataSubject = new BehaviorSubject<UserModel | null>(this.getUser());
  public readonly userData$: Observable<UserModel | null> = this.userDataSubject.asObservable();
  public readonly user$: Observable<UserModel> = this.userData$.pipe(filter((user): user is UserModel => user !== null));
  private destroy$ = new Subject<void>();
  private refreshTokenTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_BUFFER_SECONDS = 60;

  constructor(
    private _accountService: AccountService,
    private _cmsAccountService: CMSAccountService,
    private router: Router,
    private sweetAlertService: SweetAlertService,
    private loaderService: LoaderHandlerService,
    private ngbModal: NgbModal
  ) {
    this.initTokenRefresh();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isLoggedIn(): boolean {
    return this.getToken() !== null && this.getUser() !== null;
  }

  setToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getUser(): UserModel | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) as UserModel : null;
  }

  public setUser(user: UserModel | null): void {
    if (user) localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    this.userDataSubject.next(user);
  }

  login(data: ILoginResponse): Observable<void> {
    this.storeTokens(data);
    const decoded: IDecodedToken = this.decodeToken(data.token);
    return this.getAccountDetails(decoded);
  }

  decodeToken(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace('-', '+').replace('_', '/');
      return JSON.parse(atob(base64));
    } catch (error) {
      return null;
    }
  }

  getAccountDetails(decoded: IDecodedToken): Observable<void> {
    const role = decoded.Role.toLowerCase();
    if (role === Role.CLIENT || role === Role.SERVICE_PROVIDER) {
      return this._cmsAccountService.getOneAccount()
        .pipe(
          takeUntil(this.destroy$),
          map((res) => {
            if (res.data) {
              this.finalizeUser(decoded, res.data);
            } else {
              this.errorHandler();
            }
          }),
          catchError(() => {
            this.errorHandler();
            return of(undefined);
          })
        );
    } else {
      this.finalizeUser(decoded);
      return of(undefined);
    }
  }

  private finalizeUser(
    decoded: IDecodedToken,
    accountDetailsData?: IAccount
  ) {
    const userData: UserModel = {
      role: decoded.Role.toLowerCase() as Role,
      email: decoded.Email,
      authId: decoded.UserId,
      id: accountDetailsData?.id || decoded.UserId,
      name: accountDetailsData?.clientName || accountDetailsData?.serviceProviderName || decoded.UserName || decoded.Email,
      subRoles: decoded.SubRoles,
      parentAuthId: decoded.ParentAuthId,
      isSubUser: decoded.IsSubUser === 'True' ? true : false,
      twoFactorEnabled: decoded.TwoFactorEnabled === 'True' ? true : false,
      country: accountDetailsData?.country || undefined
    };
    
    this.setUser(userData);
    this.router.navigateByUrl(this.getReturnUrl());
  }

  getReturnUrl(): string {
    const urlTree = this.router.parseUrl(this.router.url);
    const returnUrl = urlTree.queryParams['return_url'];

    if (typeof returnUrl === 'string' && returnUrl.startsWith('/')) {
      return returnUrl;
    }

    return '/';
  }

  errorHandler() {
    this.sweetAlertService.showAlert('Failed to fetch user details', AlertType.Error);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  }

  private initTokenRefresh(): void {
    const token = this.getToken();
    if (!token) return;
    const decoded: IDecodedToken = this.decodeToken(token);
    if (!decoded?.exp) return;
    const remainingSeconds = decoded.exp - Math.floor(Date.now() / 1000);
    if (remainingSeconds <= 0) return;
    this.scheduleTokenRefresh(remainingSeconds);
  }

  private storeTokens(res: ILoginResponse): void {
    this.setToken(res.token);
    if (res.refreshToken) this.setRefreshToken(res.refreshToken);
    const expiresIn = Math.floor((new Date(res.expires_in).getTime() - Date.now()) / 1000);
    if (expiresIn) this.scheduleTokenRefresh(expiresIn);
  }

  private scheduleTokenRefresh(expiresInSeconds: number): void {
    this.cancelTokenRefresh();
    const refreshInMs = Math.max((expiresInSeconds - this.REFRESH_BUFFER_SECONDS) * 1000, 0);
    this.refreshTokenTimer = setTimeout(() => this.doRefreshToken(), refreshInMs);
  }

  private cancelTokenRefresh(): void {
    if (this.refreshTokenTimer !== null) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }
  }

  private doRefreshToken(): void {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return;
    }

    this._accountService.refreshToken(refreshToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.storeTokens(res.data);
          } else {
            this.logout();
          }
        },
        error: () => this.logout()
      });
  }

  logout(redirectUrl?: string) {
    if (!this.isLoggedIn()) {
      this.clearUserData(redirectUrl);
      return;
    }

    this.loaderService.startLoading();

    this._accountService.logoutUser()
      .pipe(
        finalize(() => {
          this.loaderService.stopLoading();
          this.clearUserData(redirectUrl);
        })
      )
      .subscribe();
  }

  private clearUserData(redirectUrl?: string) {
    this.cancelTokenRefresh();
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.API_KEY);
    this.setUser(null);
    // this.injector.get(ClientAccessService).updateApiKey(null);

    // Close all/any open modals
    this.ngbModal.dismissAll();
    this.sweetAlertService.closePopup();

    this.router.navigateByUrl(redirectUrl ?? '/auth/login');
  }
}
